import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, LayoutGrid, FileDown, Star } from 'lucide-react';
import { moduleApi } from '../../../lib/api';
import { generateTableQrSheetPdf } from '../../../lib/tableQrSheetPdfGenerator';

interface EvenementielEvent {
    id: string;
    type: 'PRIVÉ' | 'PROFESSIONNEL';
    first_name?: string;
    last_name?: string;
    company_name?: string;
    start_time: string;
}

interface Collecte {
    tables_planned_count: number;
    table_numbers: string[];
    vip_tables: string[];
    public_token: string;
}

const eventLabel = (e: EvenementielEvent) => e.type === 'PRIVÉ' ? `${e.first_name || ''} ${e.last_name || ''}`.trim() : (e.company_name || '');

/** Page dédiée "Tables & QR codes (Jour J)" — sortie de la page de configuration du menu, qui devenait trop
 *  chargée pour un réglage utilisé un jour précis. Accessible via un bouton "QR codes" bien visible sur la
 *  page de suivi, au même niveau que "Mode terrain". Demande explicite d'Emeni (2026-08-22) : "cette partie
 *  là ne doit pas être dans les paramètres mais un bouton bien visible QR code". */
export function QrCodesPage() {
    const { eventId } = useParams<{ eventId: string }>();
    const navigate = useNavigate();
    const [event, setEvent] = React.useState<EvenementielEvent | null>(null);
    const [collecte, setCollecte] = React.useState<Collecte | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState('');
    const [tablesDraft, setTablesDraft] = React.useState('');
    const [savingTables, setSavingTables] = React.useState(false);
    const [tableNumbersDraft, setTableNumbersDraft] = React.useState<string[]>([]);
    const [vipTablesDraft, setVipTablesDraft] = React.useState<string[]>([]);
    const [newTableDraft, setNewTableDraft] = React.useState('');
    const [savingTableNumbers, setSavingTableNumbers] = React.useState(false);
    const [qrWidthValue, setQrWidthValue] = React.useState('7');
    const [qrHeightValue, setQrHeightValue] = React.useState('8.4');
    const [qrSizeUnit, setQrSizeUnit] = React.useState<'cm' | 'mm'>('cm');
    const [qrTitle, setQrTitle] = React.useState('DÉCLAREZ VOS RESTRICTIONS ALIMENTAIRES');
    const [generatingQr, setGeneratingQr] = React.useState(false);
    const [liveCheckins, setLiveCheckins] = React.useState<{ total_checked_in: number; tables: unknown[] } | null>(null);

    const load = React.useCallback(() => {
        if (!eventId) return;
        setLoading(true);
        Promise.all([
            moduleApi.getEvenementielEvent(eventId),
            moduleApi.getEvenementielCollecte(eventId),
        ]).then(([ev, col]) => {
            setEvent(ev);
            setCollecte(col);
            setTablesDraft(String(col?.tables_planned_count ?? 0));
            setTableNumbersDraft(Array.isArray(col?.table_numbers) ? col.table_numbers : []);
            setVipTablesDraft(Array.isArray(col?.vip_tables) ? col.vip_tables : []);
        }).catch((e: any) => setError(e?.message || 'Erreur de chargement.')).finally(() => setLoading(false));
    }, [eventId]);

    React.useEffect(() => { load(); }, [load]);

    React.useEffect(() => {
        if (!eventId || tableNumbersDraft.length === 0) return;
        let cancelled = false;
        const poll = () => {
            moduleApi.getEvenementielCollecteLiveCheckins(eventId).then((d: any) => { if (!cancelled) setLiveCheckins(d); }).catch(() => {});
        };
        poll();
        const interval = setInterval(poll, 25000);
        return () => { cancelled = true; clearInterval(interval); };
    }, [eventId, tableNumbersDraft.length]);

    const saveTables = async () => {
        if (!eventId) return;
        setSavingTables(true);
        setError('');
        try {
            await moduleApi.updateEvenementielCollecte(eventId, { tables_planned_count: Number(tablesDraft) || 0 });
            load();
        } catch (e: any) {
            setError(e?.message || "Erreur lors de l'enregistrement.");
        } finally {
            setSavingTables(false);
        }
    };

    const addTableNumber = () => {
        const v = newTableDraft.trim();
        if (!v || tableNumbersDraft.includes(v)) { setNewTableDraft(''); return; }
        setTableNumbersDraft((prev) => [...prev, v]);
        setNewTableDraft('');
    };
    const removeTableNumber = (t: string) => {
        setTableNumbersDraft((prev) => prev.filter((x) => x !== t));
        setVipTablesDraft((prev) => prev.filter((x) => x !== t));
    };
    // Bascule VIP — une table VIP apparaît toujours en tête de liste en Mode terrain, avant les autres.
    // Demande explicite d'Emeni (2026-08-23) : "on devrait pouvoir créer des tables VIP qui apparaîtront tout
    // en haut de la liste des tables en mode terrain".
    const toggleVipTable = (t: string) => setVipTablesDraft((prev) => prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]);

    const saveTableNumbers = async () => {
        if (!eventId) return;
        setSavingTableNumbers(true);
        setError('');
        try {
            await moduleApi.updateEvenementielCollecte(eventId, { table_numbers: tableNumbersDraft, vip_tables: vipTablesDraft });
            load();
        } catch (e: any) {
            setError(e?.message || "Erreur lors de l'enregistrement des tables.");
        } finally {
            setSavingTableNumbers(false);
        }
    };

    const handleGenerateQrSheet = async () => {
        if (!collecte?.public_token || tableNumbersDraft.length === 0) return;
        setGeneratingQr(true);
        try {
            const unitFactor = qrSizeUnit === 'cm' ? 10 : 1;
            const cardWidthMm = (Number(qrWidthValue) || 7) * unitFactor;
            const cardHeightMm = (Number(qrHeightValue) || 8.4) * unitFactor;
            await generateTableQrSheetPdf({
                eventLabel: event ? eventLabel(event) : 'Privatisation',
                tables: tableNumbersDraft,
                baseUrl: `${window.location.origin}/ma-table/${collecte.public_token}`,
                cardWidthMm,
                cardHeightMm,
                title: qrTitle,
            });
        } finally {
            setGeneratingQr(false);
        }
    };

    if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-content-muted" size={24} /></div>;
    if (!event || !collecte) return <p className="text-sm text-content-muted">Événement introuvable.</p>;

    return (
        <div className="space-y-6 max-w-2xl">
            <div>
                <button onClick={() => navigate(`/menus-convives/${event.id}`)} className="flex items-center gap-1.5 text-xs font-bold text-content-muted hover:text-content mb-3">
                    <ArrowLeft size={14} /> Retour au suivi
                </button>
                <h1 className="text-xl font-bold text-content tracking-tight">{eventLabel(event)} — Tables & QR codes</h1>
                <p className="text-content-muted text-sm mt-0.5">
                    {new Date(event.start_time).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                </p>
            </div>

            {error && <p className="text-xs text-red-500 font-semibold">{error}</p>}

            <div className="bg-surface-soft border border-border rounded-2xl p-4 space-y-3">
                <h3 className="text-sm font-bold text-content flex items-center gap-2"><LayoutGrid size={15} /> Tables</h3>
                <div className="flex items-center gap-2">
                    <input type="number" min={0} value={tablesDraft} onChange={(e) => setTablesDraft(e.target.value)}
                        className="w-24 bg-surface border border-border rounded-md px-2.5 py-2 text-sm text-content outline-none focus:border-brand-500" />
                    <span className="text-xs text-content-muted">tables prévues pour le service</span>
                    <button onClick={saveTables} disabled={savingTables || Number(tablesDraft) === collecte.tables_planned_count}
                        className="ml-auto px-3 py-1.5 rounded-lg bg-brand-600 hover:bg-brand-700 text-brand-fg font-bold text-xs disabled:opacity-40">
                        {savingTables ? 'Enregistrement...' : 'Enregistrer'}
                    </button>
                </div>
                <div className="pt-2 border-t border-border space-y-2">
                    <p className="text-[11px] font-bold text-content-muted uppercase tracking-widest">Tables nommées (Jour J)</p>
                    <p className="text-[11px] text-content-muted">Ajoute un numéro ou un nom par table (ex: "1", "2", "VIP") pour générer un QR à coller dessus — le convive scanne pour se déclarer assis à cette table.</p>
                    <div className="flex flex-wrap gap-1.5">
                        {tableNumbersDraft.map((t) => {
                            const isVip = vipTablesDraft.includes(t);
                            return (
                                <span key={t} className={`flex items-center gap-1 pl-1 pr-1 py-1 rounded-full border text-xs font-bold ${isVip ? 'bg-amber-500/10 border-amber-500/30 text-amber-700 dark:text-amber-400' : 'bg-surface border-border text-content'}`}>
                                    <button type="button" onClick={() => toggleVipTable(t)} title={isVip ? 'Retirer VIP' : 'Marquer VIP'}
                                        className={`p-1 rounded-full ${isVip ? 'text-amber-500' : 'text-content-muted hover:text-amber-500'}`}>
                                        <Star size={12} fill={isVip ? 'currentColor' : 'none'} />
                                    </button>
                                    Table {t}
                                    <button type="button" onClick={() => removeTableNumber(t)} className="p-0.5 rounded-full hover:bg-red-500/10 text-content-muted hover:text-red-400">×</button>
                                </span>
                            );
                        })}
                    </div>
                    <div className="flex items-center gap-2">
                        <input value={newTableDraft} onChange={(e) => setNewTableDraft(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTableNumber(); } }}
                            placeholder="Ex: 1, VIP..." className="flex-1 min-w-0 bg-surface border border-border rounded-md px-2.5 py-2 text-sm text-content outline-none focus:border-brand-500" />
                        <button onClick={addTableNumber} className="px-3 py-2 rounded-lg border border-border text-xs font-bold text-content hover:bg-surface transition-colors">Ajouter</button>
                        <button onClick={saveTableNumbers} disabled={savingTableNumbers}
                            className="px-3 py-2 rounded-lg bg-brand-600 hover:bg-brand-700 text-brand-fg font-bold text-xs disabled:opacity-40">
                            {savingTableNumbers ? 'Enregistrement...' : 'Enregistrer'}
                        </button>
                    </div>
                    {tableNumbersDraft.length > 0 && (
                        <div className="space-y-2 pt-1">
                            <div className="space-y-1">
                                <label className="text-[11px] text-content-muted">Titre affiché au-dessus de chaque QR</label>
                                <input value={qrTitle} onChange={(e) => setQrTitle(e.target.value)}
                                    className="w-full bg-surface border border-border rounded-md px-2.5 py-2 text-xs font-bold text-content outline-none focus:border-brand-500" />
                            </div>
                            <div className="flex items-center gap-2 flex-wrap">
                                <label className="text-[11px] text-content-muted">Largeur</label>
                                <input type="number" min={1} step="0.1" value={qrWidthValue} onChange={(e) => setQrWidthValue(e.target.value)}
                                    className="w-20 bg-surface border border-border rounded-md px-2 py-1.5 text-xs text-content outline-none focus:border-brand-500" />
                                <label className="text-[11px] text-content-muted">Hauteur</label>
                                <input type="number" min={1} step="0.1" value={qrHeightValue} onChange={(e) => setQrHeightValue(e.target.value)}
                                    className="w-20 bg-surface border border-border rounded-md px-2 py-1.5 text-xs text-content outline-none focus:border-brand-500" />
                                <select value={qrSizeUnit} onChange={(e) => setQrSizeUnit(e.target.value as 'cm' | 'mm')}
                                    className="bg-surface border border-border rounded-md px-2 py-1.5 text-xs text-content outline-none focus:border-brand-500">
                                    <option value="cm">cm</option>
                                    <option value="mm">mm</option>
                                </select>
                                <button onClick={handleGenerateQrSheet} disabled={generatingQr}
                                    className="ml-auto flex items-center gap-1.5 px-3 py-2 rounded-lg border border-border text-xs font-bold text-content hover:bg-surface transition-colors disabled:opacity-50">
                                    {generatingQr ? <Loader2 size={13} className="animate-spin" /> : <FileDown size={13} />} Générer les QR codes des tables
                                </button>
                            </div>
                        </div>
                    )}
                    {liveCheckins && liveCheckins.tables.length > 0 && (
                        <div className="pt-3">
                            <button type="button" onClick={() => navigate(`/mode-terrain/${event.id}`)}
                                className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-brand-600 text-brand-fg text-xs font-black uppercase tracking-wide">
                                Mode terrain — {liveCheckins.total_checked_in} convive{liveCheckins.total_checked_in > 1 ? 's' : ''} installé{liveCheckins.total_checked_in > 1 ? 's' : ''}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
